/**
 * The connected-app dialogs, shared by the register and an application's own
 * page: registering or editing one step by step (`WizardDialog`), and the
 * secrets the server shows once.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AppWindow,
  BadgeCheck,
  Check,
  ClipboardCheck,
  Copy,
  KeyRound,
  LogIn,
  Pencil,
  PlugZap,
  ShieldCheck,
  Webhook,
} from 'lucide-react';
import {
  Alert,
  ApiError,
  Badge,
  Button,
  Checkbox,
  Input,
  Modal,
  Textarea,
  WizardDialog,
  api,
  toast,
  type WizardStep,
} from '@connect/web-shared';

import {
  APPS,
  appKeys,
  bodyOf,
  emptyForm,
  formOf,
  type AppForm,
  type ConnectedApp,
  type ScopeDef,
  type Secrets,
} from './connected-apps';

export function AppDialog({
  app,
  scopes,
  onClose,
  onSecrets,
}: {
  app: ConnectedApp | null;
  scopes: ScopeDef[];
  onClose: () => void;
  onSecrets: (name: string, secrets: Secrets) => void;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<AppForm>(app ? formOf(app) : emptyForm());
  const save = useMutation({
    mutationFn: () =>
      app
        ? api.patch<ConnectedApp & Secrets>(`${APPS}/${app.public_id}`, bodyOf(form))
        : api.post<ConnectedApp & Secrets>(APPS, bodyOf(form)),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: appKeys.all });
      onClose();
      if (saved.client_secret || saved.webhook_secret) {
        onSecrets(saved.name, { client_secret: saved.client_secret, webhook_secret: saved.webhook_secret });
      } else {
        toast.success('Application saved');
      }
    },
  });
  const set = <K extends keyof AppForm>(key: K, value: AppForm[K]) =>
    setForm((current) => ({ ...current, [key]: value }));
  const labels = Object.fromEntries(scopes.map((s) => [s.name, s.label]));
  const redirects = bodyOf(form).redirect_uris as string[];

  const steps: WizardStep[] = [
    {
      id: 'application',
      title: 'Application',
      hint: 'Name and homepage',
      icon: <AppWindow />,
      heading: 'What is the application?',
      description: 'Clients read this on the consent page before they connect it.',
      blocker: !form.name.trim()
        ? 'Give the application a name.'
        : !form.homepage_url.trim()
          ? 'Give its homepage.'
          : null,
      content: (
        <>
          <Input label="Name" required value={form.name} onChange={(e) => set('name', e.target.value)} />
          <Textarea
            label="What it does"
            hint="One or two sentences, in the client's words."
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
          />
          <Input
            label="Homepage"
            required
            placeholder="https://"
            value={form.homepage_url}
            onChange={(e) => set('homepage_url', e.target.value)}
          />
          <Input
            label="Logo address"
            placeholder="https://"
            hint="A square image, shown beside its name."
            value={form.logo_url}
            onChange={(e) => set('logo_url', e.target.value)}
          />
        </>
      ),
    },
    {
      id: 'sign-in',
      title: 'Sign-in',
      hint: 'Where clients return to',
      icon: <LogIn />,
      heading: 'Where may Connect send clients back?',
      description:
        'After a client allows or cancels, Connect sends it back to one of these addresses — compared exactly. Any other is refused, and nothing is sent there.',
      blocker: redirects.length ? null : 'Add at least one redirect address.',
      content: (
        <Textarea
          label="Redirect addresses"
          required
          rows={4}
          hint="One per line. https://, or http://localhost for a program on a computer."
          value={form.redirect_uris}
          onChange={(e) => set('redirect_uris', e.target.value)}
        />
      ),
    },
    {
      id: 'access',
      title: 'Access',
      hint: 'What it may ask for',
      icon: <ShieldCheck />,
      heading: 'What may it ask a client for?',
      description:
        'A client sees each of these on the consent page, and gives only what its own account can use.',
      blocker: form.scopes.length ? null : 'Choose at least one thing it may ask for.',
      content: (
        <div className="space-y-2">
          {scopes.map((scope) => (
            <Checkbox
              key={scope.name}
              label={scope.label}
              hint={scope.name}
              checked={form.scopes.includes(scope.name)}
              onChange={(e) =>
                set(
                  'scopes',
                  e.target.checked
                    ? [...form.scopes, scope.name]
                    : form.scopes.filter((name) => name !== scope.name),
                )
              }
            />
          ))}
        </div>
      ),
    },
    {
      id: 'events',
      title: 'Events',
      hint: 'Its webhook address',
      icon: <Webhook />,
      heading: 'Where does it hear about its connections?',
      description:
        'Optional. Template events of the clients that allowed it, and each disconnection, are sent here, signed. Its signing secret is shown once.',
      content: (
        <Input
          label="Webhook address"
          placeholder="https://"
          value={form.webhook_url}
          onChange={(e) => set('webhook_url', e.target.value)}
        />
      ),
    },
    {
      id: 'trust',
      title: 'Policies',
      hint: 'Terms, verified, listed',
      icon: <BadgeCheck />,
      heading: 'Its policies, and how it is shown',
      description:
        "The application's own privacy policy and terms — not Connect's. Clients see them as links on the consent page, under what it asks for, before they allow it.",
      content: (
        <>
          <Input
            label="Privacy policy"
            placeholder="https://"
            value={form.privacy_url}
            onChange={(e) => set('privacy_url', e.target.value)}
          />
          <Input
            label="Terms"
            placeholder="https://"
            value={form.terms_url}
            onChange={(e) => set('terms_url', e.target.value)}
          />
          <Checkbox
            label="Verified"
            hint="You checked who runs it (company, contact, website). Clients see a “Verified by Connect” mark beside its name. It changes nothing it may do."
            checked={form.verified}
            onChange={(e) => set('verified', e.target.checked)}
          />
          <Checkbox
            label="Listed"
            hint="Kept for a directory of applications clients can browse, which Connect does not have yet: today every application is connected by starting from the application itself, listed or not."
            checked={form.listed}
            onChange={(e) => set('listed', e.target.checked)}
          />
        </>
      ),
    },
    {
      id: 'review',
      title: 'Review',
      hint: app ? 'Check, then save' : 'Check, then register',
      icon: <ClipboardCheck />,
      heading: app ? 'Check, then save' : 'Check, then register',
      description: 'Everything at a glance. Edit any part to go back to its step.',
      content: (openStep) => (
        <div className="space-y-4">
          <div className="flex items-start gap-4 rounded-card border border-line bg-surface-2/50 p-4">
            {form.logo_url.trim() ? (
              <img src={form.logo_url.trim()} alt="" className="size-14 shrink-0 rounded-card border border-line bg-surface object-contain" />
            ) : (
              <span className="grid size-14 shrink-0 place-items-center rounded-card bg-accent-subtle text-xl font-semibold text-accent">
                {(form.name.trim() || '?').charAt(0).toUpperCase()}
              </span>
            )}
            <div className="min-w-0 flex-1 space-y-1">
              <p className="flex items-center gap-1.5 text-base font-semibold text-heading">
                {form.name.trim()}
                {form.verified ? <BadgeCheck className="size-4 text-accent" aria-label="Verified" /> : null}
              </p>
              <p className="break-all text-xs text-accent">{form.homepage_url.trim()}</p>
              {form.description.trim() ? <p className="text-sm text-body">{form.description.trim()}</p> : null}
            </div>
            <EditLink onClick={() => openStep('application')} />
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <ReviewBlock icon={<LogIn />} title="Sign-in" onEdit={() => openStep('sign-in')}>
              <p className="text-xs text-muted">Clients are sent back only to</p>
              <ul className="space-y-1">
                {redirects.map((uri) => (
                  <li key={uri} className="break-all rounded-control bg-surface-2 px-2 py-1 font-mono text-xs text-body">
                    {uri}
                  </li>
                ))}
              </ul>
            </ReviewBlock>

            <ReviewBlock icon={<ShieldCheck />} title="Access" onEdit={() => openStep('access')}>
              <p className="text-xs text-muted">It may ask a client to</p>
              <ul className="space-y-1.5">
                {form.scopes.map((name) => (
                  <li key={name} className="flex items-start gap-2 text-sm text-body">
                    <Check className="mt-0.5 size-3.5 shrink-0 text-success" aria-hidden />
                    {labels[name] ?? name}
                  </li>
                ))}
              </ul>
            </ReviewBlock>

            <ReviewBlock icon={<Webhook />} title="Events" onEdit={() => openStep('events')}>
              {form.webhook_url.trim() ? (
                <>
                  <p className="text-xs text-muted">Its events are sent, signed, to</p>
                  <p className="break-all rounded-control bg-surface-2 px-2 py-1 font-mono text-xs text-body">
                    {form.webhook_url.trim()}
                  </p>
                </>
              ) : (
                <p className="text-sm text-muted">No webhook address: it will not hear about its connections.</p>
              )}
            </ReviewBlock>

            <ReviewBlock icon={<BadgeCheck />} title="Policies" onEdit={() => openStep('trust')}>
              <div className="flex flex-wrap gap-1.5">
                <Badge tone={form.verified ? 'success' : 'neutral'}>{form.verified ? 'Verified' : 'Not verified'}</Badge>
                <Badge tone="neutral">{form.listed ? 'Listed' : 'Unlisted'}</Badge>
              </div>
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                <dt className="text-muted">Privacy policy</dt>
                <dd className="break-all text-body">{form.privacy_url.trim() || 'Not given'}</dd>
                <dt className="text-muted">Terms</dt>
                <dd className="break-all text-body">{form.terms_url.trim() || 'Not given'}</dd>
              </dl>
            </ReviewBlock>
          </div>

          {app ? null : (
            <Alert tone="info" title="It waits for approval">
              Once registered, clients cannot connect it and it cannot get a token until you approve it. Its secrets are
              shown once, next.
            </Alert>
          )}
        </div>
      ),
    },
  ];

  return (
    <WizardDialog
      open
      onClose={onClose}
      title={app ? `Edit ${app.name}` : 'Register an application'}
      icon={<PlugZap />}
      steps={steps}
      reachable={app !== null}
      finishLabel={app ? 'Save' : 'Register'}
      busy={save.isPending}
      onFinish={() => save.mutate()}
      error={
        save.error ? (
          <Alert tone="danger" title="Could not save">
            {save.error instanceof ApiError
              ? [save.error.message, ...save.error.details.map((d) => d.message)].join(' ')
              : 'Could not reach the server. Try again.'}
          </Alert>
        ) : null
      }
      aside={
        <div className="space-y-1.5 rounded-control border border-line p-3">
          <p className="text-sm font-medium text-heading">{form.name.trim() || 'Your application'}</p>
          <p className="text-xs text-muted">
            {redirects.length} redirect address{redirects.length === 1 ? '' : 'es'} · {form.scopes.length} thing
            {form.scopes.length === 1 ? '' : 's'} it may ask for
          </p>
          <p className="text-xs text-muted">{form.webhook_url.trim() ? 'Hears events' : 'No webhook address'}</p>
        </div>
      }
    />
  );
}

export function SecretsDialog({ name, secrets, onClose }: { name: string; secrets: Secrets; onClose: () => void }) {
  return (
    <Modal
      open
      onClose={onClose}
      title={`${name}: secrets`}
      description="Shown once. Give them to the application's developer by a safe channel; Connect cannot show them again."
      dismissOnBackdrop={false}
      footer={
        <Button size="sm" onClick={onClose}>
          Done
        </Button>
      }
    >
      <div className="space-y-3">
        {secrets.client_secret ? <Secret label="Client secret" value={secrets.client_secret} /> : null}
        {secrets.webhook_secret ? <Secret label="Webhook signing secret" value={secrets.webhook_secret} /> : null}
      </div>
    </Modal>
  );
}

function Secret({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Alert tone="warning" title={`${label} — shown once`}>
      <span className="mt-1.5 flex items-center gap-2">
        <KeyRound className="size-3.5 shrink-0" aria-hidden />
        <code className="min-w-0 flex-1 truncate font-mono text-sm">{value}</code>
        <Button
          variant="secondary"
          size="sm"
          aria-label={`Copy the ${label.toLowerCase()}`}
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(value);
              setCopied(true);
            } catch {
              toast.error('Could not copy', 'Select it and copy it by hand.');
            }
          }}
        >
          {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
        </Button>
      </span>
    </Alert>
  );
}

function EditLink({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex shrink-0 items-center gap-1 rounded-control px-2 py-1 text-xs font-medium text-accent transition-colors hover:bg-accent-subtle"
    >
      <Pencil className="size-3" aria-hidden />
      Edit
    </button>
  );
}

/** One step's part of the review, with the way back to that step. */
function ReviewBlock({
  icon,
  title,
  onEdit,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  onEdit: () => void;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-2.5 rounded-card border border-line p-4">
      <header className="flex items-center justify-between gap-2">
        <h4 className="flex items-center gap-2 text-sm font-semibold text-heading">
          <span className="grid size-7 place-items-center rounded-control bg-accent-subtle text-accent [&_svg]:size-3.5">
            {icon}
          </span>
          {title}
        </h4>
        <EditLink onClick={onEdit} />
      </header>
      {children}
    </section>
  );
}
